import React, { useState, useEffect, useCallback } from 'react';
import { FaShoppingCart, FaArrowRight } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GOVERNORATES, getShippingCost } from '../../data/governorates';
import firebaseService from '../../services/firebaseService';
import emailService from '../../services/emailService';
import simpleNotification from '../../utils/simpleNotification';
import { useCustomAlert } from '../../hooks/useCustomAlert';
import CustomAlert from '../CustomAlert/CustomAlert';
import { useLoadingContext } from '../Loading/LoadingProvider';
import './Cart.css';

function Cart() {
  const navigate = useNavigate();
  const { alertState, showAlert: showCustomAlert } = useCustomAlert();
  const { startComponentLoading, stopComponentLoading, isComponentLoading } = useLoadingContext();

  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1: Info, 2: Review

  const [customerData, setCustomerData] = useState({
    fullName: '',
    lastName: '',
    email: '',
    governorate: '',
    address: '',
    phone1: '',
    phone2: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Helper function to round to nearest 5 or 0
  const roundPrice = (price) => {
    return Math.round(price / 5) * 5;
  };

  const validateForm = () => {
    const errors = {};
    if (!customerData.fullName.trim()) errors.fullName = 'First name is required';
    if (!customerData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!customerData.governorate) errors.governorate = 'Governorate is required';
    if (!customerData.address.trim()) errors.address = 'Address is required';

    const phoneRegex = /^01[0-9]{9}$/;
    if (!phoneRegex.test(customerData.phone1)) errors.phone1 = 'Invalid phone number';
    if (!phoneRegex.test(customerData.phone2)) errors.phone2 = 'Invalid phone number';

    if (customerData.phone1 && customerData.phone2 && customerData.phone1 === customerData.phone2) {
      errors.phone2 = 'Phone numbers must be different';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateTotals = useCallback((items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setTotalPrice(subtotal);

    let currentDiscount = 0;
    if (appliedPromo) {
      if (appliedPromo.discountType === 'percentage') {
        currentDiscount = (subtotal * appliedPromo.discountValue) / 100;
      } else {
        currentDiscount = appliedPromo.discountValue;
      }
      currentDiscount = Math.min(currentDiscount, subtotal);
    }
    setDiscount(currentDiscount);

    const cost = customerData.governorate ? getShippingCost(customerData.governorate) : 0;
    setShippingCost(cost);

    // Apply rounding to the final total
    const rawTotal = subtotal + cost - currentDiscount;
    setFinalTotal(roundPrice(rawTotal));
  }, [customerData.governorate, appliedPromo]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(items);
    calculateTotals(items);
  }, [calculateTotals]);

  // Scroll to top when checkout step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [checkoutStep]);

  const handleCustomerDataChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = (name === 'phone1' || name === 'phone2') ? value.replace(/[^0-9]/g, '') : value;
    setCustomerData(prev => ({ ...prev, [name]: cleanValue }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await firebaseService.validatePromoCode(promoCode, totalPrice);
      if (result.success) {
        setAppliedPromo(result.promo);
        setDiscount(result.discount);
        // Removed the large alert message here as per user request
      } else {
        setPromoError(result.error);
        setAppliedPromo(null);
        setDiscount(0);
      }
    } catch (e) {
      setPromoError('Error validating code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!validateForm()) return;
    try {
      startComponentLoading('submitOrder', 'Processing your order...');
      await new Promise(r => setTimeout(r, 1500));

      const order = {
        id: Date.now().toString(),
        customer: customerData,
        items: cartItems,
        subtotal: totalPrice,
        shipping: shippingCost,
        discount: discount,
        appliedPromo: appliedPromo ? { code: appliedPromo.code, value: appliedPromo.discountValue } : null,
        total: finalTotal,
        orderDate: new Date().toISOString(),
        status: 'pending'
      };

      const result = await firebaseService.addOrder(order);
      if (result.success) {
        if (appliedPromo) await firebaseService.usePromoCode(appliedPromo.id);
        try { await emailService.sendNewOrderEmail(order); } catch (e) { }
        simpleNotification.triggerNewOrderEvent(order);
        localStorage.removeItem('cart');
        window.dispatchEvent(new CustomEvent('cartUpdated'));
        navigate('/order-confirmation', { state: { order } });
      } else {
        showCustomAlert('Error: ' + result.error, 'error');
      }
    } catch (e) {
      console.error(e);
    } finally {
      stopComponentLoading('submitOrder');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-empty">
        <div className="cart-empty-content">
          <FaShoppingCart className="cart-empty-icon" />
          <h2>Your cart is empty</h2>
          <p>Add some beautiful posters to your cart to get started!</p>
          <Link to="/shop" className="shop-now-btn">Shop Now</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-container">
      {/* Checkout Steps */}
      <div className="checkout-progress">
        <div className={`progress-step ${checkoutStep >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <span>Information</span>
        </div>
        <div className="progress-step">
          <FaArrowRight style={{ opacity: 0.3 }} />
        </div>
        <div className={`progress-step ${checkoutStep >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <span>Review</span>
        </div>
      </div>

      <div className="checkout-content">
        <div className="billing-section">
          <AnimatePresence mode="wait">
            {checkoutStep === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="billing-form"
              >
                <h2 className="billing-title">Customer Information</h2>

                {/* Mobile: Product Card at Top */}
                <div className="mobile-only-products" style={{ marginBottom: '30px' }}>
                  {cartItems.map(item => (
                    <div key={item.id} className="product-card" style={{ marginBottom: '15px' }}>
                      <div className="product-image">
                        <img src={item.isCustom && item.customImage ? item.customImage : item.image} alt={item.name} />
                      </div>
                      <div className="product-info">
                        <h4 className="product-name">{item.name}</h4>
                        <p className="product-details">
                          {item.isCustom ? 'Custom Poster' : item.category} | {item.size}
                          {item.isCustom && item.orientation && (
                            <span style={{ marginLeft: '6px', color: '#c5a059', fontSize: '0.75em' }}>
                              | {item.orientation === 'vertical' ? 'عمودي' : 'أفقي'}
                            </span>
                          )}
                        </p>
                        <div className="product-price">{item.price * item.quantity} EGP</div>
                      </div>
                      <button className="remove-item-btn" onClick={() => {
                        const updated = cartItems.filter(i => i.id !== item.id);
                        setCartItems(updated);
                        localStorage.setItem('cart', JSON.stringify(updated));
                        window.dispatchEvent(new CustomEvent('cartUpdated'));
                      }}>✕</button>
                    </div>
                  ))}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input name="fullName" value={customerData.fullName} onChange={handleCustomerDataChange} className={formErrors.fullName ? 'error' : ''} placeholder="Enter your first name" />
                    {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input name="lastName" value={customerData.lastName} onChange={handleCustomerDataChange} className={formErrors.lastName ? 'error' : ''} placeholder="Enter your last name" />
                    {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number 1 *</label>
                    <input name="phone1" value={customerData.phone1} onChange={handleCustomerDataChange} maxLength="11" placeholder="01xxxxxxxxx" className={formErrors.phone1 ? 'error' : ''} />
                    {formErrors.phone1 && <span className="error-message">{formErrors.phone1}</span>}
                  </div>
                  <div className="form-group">
                    <label>Additional Phone *</label>
                    <input name="phone2" value={customerData.phone2} onChange={handleCustomerDataChange} maxLength="11" placeholder="01xxxxxxxxx" className={formErrors.phone2 ? 'error' : ''} />
                    {formErrors.phone2 && <span className="error-message">{formErrors.phone2}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label>Governorate *</label>
                  <select name="governorate" value={customerData.governorate} onChange={handleCustomerDataChange} className={formErrors.governorate ? 'error' : ''}>
                    <option value="">Select Governorate</option>
                    {GOVERNORATES.map(g => <option key={g.value} value={g.value}>{g.name}</option>)}
                  </select>
                  {formErrors.governorate && <span className="error-message">{formErrors.governorate}</span>}
                </div>

                <div className="form-group">
                  <label>Detailed Address *</label>
                  <input name="address" value={customerData.address} onChange={handleCustomerDataChange} placeholder="Street, area, house number..." className={formErrors.address ? 'error' : ''} />
                  {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                </div>

                <div className="form-group">
                  <label>Email Address (Optional)</label>
                  <input name="email" value={customerData.email} onChange={handleCustomerDataChange} placeholder="example@mail.com" />
                </div>

                {/* Mobile: Order Summary before Button */}
                <div className="mobile-only-summary" style={{ marginTop: '30px' }}>
                  <div className="order-summary-card" style={{ margin: 0, padding: '25px' }}>
                    <h3>Order Summary</h3>
                    <div className="promo-section" style={{ marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '20px' }}>
                      {!appliedPromo ? (
                        <div className="promo-input-wrapper">
                          <input placeholder="Enter Promo Code" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} />
                          <button className="apply-promo-btn" onClick={handleApplyPromo} disabled={promoLoading}>Apply</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#4CAF50', fontWeight: 'bold', background: 'rgba(76, 175, 80, 0.1)', padding: '12px', borderRadius: '4px' }}>
                          <span>✅ Promo ({appliedPromo.code}) Applied</span>
                          <button onClick={() => { setAppliedPromo(null); setDiscount(0); }} style={{ background: 'none', border: 'none', color: '#ff4d4d', cursor: 'pointer', fontWeight: 'bold' }}>Remove</button>
                        </div>
                      )}
                      {promoError && <p className="error-message" style={{ textAlign: 'center', marginTop: '10px' }}>{promoError}</p>}
                    </div>

                    <div className="summary-details">
                      <div className="summary-row">
                        <span>Product subtotal</span>
                        <span>{totalPrice} EGP</span>
                      </div>
                      <div className="summary-row">
                        <span>Shipping cost</span>
                        <span>{shippingCost > 0 ? `${shippingCost} EGP` : 'Select governorate'}</span>
                      </div>
                      {discount > 0 && (
                        <div className="summary-row" style={{ color: '#4CAF50' }}>
                          <span>Discount</span>
                          <span>-{discount} EGP</span>
                        </div>
                      )}
                      <div className="summary-row total">
                        <span>Total Amount</span>
                        <span>{finalTotal} EGP</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button className="confirm-btn" onClick={() => validateForm() && setCheckoutStep(2)}>Continue to Review</button>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="review-step"
              >
                <h2 className="billing-title">Order Review</h2>
                <div className="review-box" style={{ background: '#252525', padding: '30px', borderRadius: '4px', border: '1px solid #444', marginBottom: '30px' }}>
                  <div style={{ marginBottom: '20px', borderBottom: '1px solid #444', paddingBottom: '15px' }}>
                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px' }}>Contact details</h4>
                    <p style={{ color: '#fff', fontSize: '1.1rem' }}>{customerData.fullName} {customerData.lastName}</p>
                    <p style={{ color: '#fff', opacity: 0.8 }}>{customerData.phone1} / {customerData.phone2}</p>
                  </div>
                  <div>
                    <h4 style={{ color: 'var(--accent-primary)', marginBottom: '10px' }}>Shipping Address</h4>
                    <p style={{ color: '#fff' }}>{customerData.address}</p>
                    <p style={{ color: '#fff', opacity: 0.8 }}>{GOVERNORATES.find(g => g.value === customerData.governorate)?.name}</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '20px' }}>
                  <button className="confirm-btn" style={{ flex: 1, background: '#333', marginTop: 0 }} onClick={() => setCheckoutStep(1)}>Edit Information</button>
                  <button className="confirm-btn" style={{ flex: 2, marginTop: 0 }} onClick={handleSubmitOrder} disabled={isComponentLoading('submitOrder')}>
                    {isComponentLoading('submitOrder') ? 'Placing Order...' : 'Confirm & Buy'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Order Summary (Desktop Only) */}
        <div className="order-summary-section desktop-only-summary">
          <div className="order-summary-card">
            <h3>Order Summary</h3>
            <div className="promo-section" style={{ marginBottom: '25px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '20px' }}>
              {!appliedPromo ? (
                <div className="promo-input-wrapper">
                  <input placeholder="Enter Promo Code" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} />
                  <button className="apply-promo-btn" onClick={handleApplyPromo} disabled={promoLoading}>Apply</button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#4CAF50', fontWeight: 'bold', background: 'rgba(76, 175, 80, 0.1)', padding: '12px', borderRadius: '4px' }}>
                  <span>✅ Promo ({appliedPromo.code}) Applied</span>
                  <button onClick={() => { setAppliedPromo(null); setDiscount(0); }} style={{ background: 'none', border: 'none', color: '#ff4d4d', cursor: 'pointer', fontWeight: 'bold' }}>Remove</button>
                </div>
              )}
              {promoError && <p className="error-message" style={{ textAlign: 'center', marginTop: '10px' }}>{promoError}</p>}
            </div>

            <div className="summary-details">
              <div className="summary-row">
                <span>Product subtotal</span>
                <span>{totalPrice} EGP</span>
              </div>
              <div className="summary-row">
                <span>Shipping cost</span>
                <span>{shippingCost > 0 ? `${shippingCost} EGP` : 'Select governorate'}</span>
              </div>
              {discount > 0 && (
                <div className="summary-row" style={{ color: '#4CAF50' }}>
                  <span>Discount</span>
                  <span>-{discount} EGP</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total Amount</span>
                <span>{finalTotal} EGP</span>
              </div>
            </div>
          </div>

          <div className="product-cards">
            {cartItems.map(item => (
              <div key={item.id} className="product-card">
                <div className="product-image">
                  <img src={item.isCustom && item.customImage ? item.customImage : item.image} alt={item.name} />
                </div>
                <div className="product-info">
                  <h4 className="product-name">{item.name}</h4>
                  <p className="product-details">
                    {item.isCustom ? 'Custom Poster' : item.category} | {item.size}
                    {item.isCustom && item.orientation && (
                      <span style={{ marginLeft: '6px', color: '#c5a059', fontSize: '0.75em' }}>
                        | {item.orientation === 'vertical' ? 'عمودي' : 'أفقي'}
                      </span>
                    )}
                  </p>
                  <div className="product-price">{item.price * item.quantity} EGP</div>
                </div>
                {checkoutStep === 1 && <button className="remove-item-btn" onClick={() => {
                  const updated = cartItems.filter(i => i.id !== item.id);
                  setCartItems(updated);
                  localStorage.setItem('cart', JSON.stringify(updated));
                  window.dispatchEvent(new CustomEvent('cartUpdated'));
                }}>✕</button>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CustomAlert {...alertState} />
    </div>
  );
}

export default Cart;
